import React, { FC, memo, useCallback, useRef, useState } from 'react'

import YouTube, {
  YouTubePlayer as IYouTubePlayer,
  YouTubeProps,
} from 'react-youtube'

import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'

import { eventObject, eventAction } from '@/constants/analytics'
import { YOUTUBE_VIDEO_ID_REGEXP, ytUrl } from '@/constants/common'
import { focusIds } from '@/constants/focus'
import { MediaPlayerBackground, MediaPlayerWrapper } from '@/containers/styles'
import { useToggleFullscreenFocus } from '@/hooks/useToggleFullscreenFocus'
import { getCurrentLanguage } from '@/i18n'
import useStore from '@/store/useStore'
import { YouTubeContentType } from '@/types/servicesTypes/configTypes'
import { sendAnalyticsEvent } from '@/utils/analyticsUtils'
import { isTVOS } from '@/utils/utils'

import { useYouTubeAnalytics } from './hooks'
import { YoutubePlayerPopupWrapper } from './styles'
import { YouTubeControls } from './YouTubeControls'

export enum PlayerState {
  UNSTARTED = -1,
  ENDED = 0,
  PLAYING = 1,
  PAUSED = 2,
  BUFFERING = 3,
  CUED = 5,
}

export type PlayerErrorsType = {
  [key: number]: string
}

const playerErrors: PlayerErrorsType = {
  2: 'The request contains an invalid parameter value',
  5: 'The requested content cannot be played in an HTML5 player or another error related to the HTML5 player has occurred',
  100: 'The video requested was not found',
  101: 'The owner of the requested video does not allow it to be played in embedded players',
  150: 'The owner of the requested video does not allow it to be played in embedded players',
}

export type YouTubePlayerType = IYouTubePlayer & {
  playerInfo?: {
    currentTime: number
    duration: number
    playerState: PlayerState
  }
}

const YouTubePlayerRaw: FC<{ youTubeContent: YouTubeContentType }> = ({
  youTubeContent,
}) => {
  const hideVideoControls = useStore((store) => store.player.hideVideoControls)
  const setYouTubeVideo = useStore((store) => store.common.setYouTubeVideo)
  const player = useRef<Nullable<YouTubePlayerType>>(null)

  const [playing, setPlaying] = useState(true)
  const [isVideoStarted, setIsVideoStarted] = useState(false)

  const { id, name, description } = youTubeContent

  const analyticsVideoId = id || '' // 'youtube$videoId-1234'

  const videoId = id?.replace(YOUTUBE_VIDEO_ID_REGEXP, '') || '' // 'videoId-1234'

  const videoUrl = `${ytUrl}${videoId}`

  useYouTubeAnalytics(
    analyticsVideoId,
    videoUrl,
    playing,
    isVideoStarted,
    player,
    name,
  )

  useToggleFullscreenFocus({ mediaContent: youTubeContent })

  const onPlayerReady: YouTubeProps['onReady'] = async (event) => {
    player.current = event.target
    event.target.loadVideoById({
      videoId: videoId,
      suggestedQuality: 'hd720',
    })
    sendVideoLoadingAnalyticsEvent()
  }

  const onPlayerError: YouTubeProps['onError'] = async (error) => {
    console.error('onPlayerError', error)
    sendVideoErrorAnalyticsEvent(error.data)
  }

  const onPlayerStateChange: YouTubeProps['onStateChange'] = async (event) => {
    isDevMode && console.log('onPlayerStateChange', event)

    if (event.data === PlayerState.BUFFERING) {
      return
    }

    if (event.data === PlayerState.ENDED) {
      setIsVideoStarted(false)
      setPlaying(false)
      hideVideoControls()
      setYouTubeVideo(null)
      return
    }

    if (event.data === PlayerState.PLAYING) {
      setPlaying(true)
      setIsVideoStarted(true)
    } else {
      setPlaying(false)
    }
  }

  /**
   * @implements
   * https://developers.google.com/youtube/player_parameters
   */
  const opts: YouTubeProps['opts'] = {
    host: 'https://www.youtube-nocookie.com',
    playerVars: {
      autoplay: 1,
      controls: 0,
      playsinline: 1,
      fs: 0,
      disablekb: 1,
      rel: 0,
      hl: getCurrentLanguage() || 'en',
      iv_load_policy: 3,
      widget_referrer: isTVOS() ? 'file://' : 'http://localhost:3000',
      origin: isTVOS() ? 'file://' : 'http://localhost:3000',
    },
  }

  const sendVideoLoadingAnalyticsEvent = useCallback(() => {
    sendAnalyticsEvent({
      object: eventObject.VIDEO,
      action: eventAction.LOADING,
      video: {
        id: analyticsVideoId,
        link: videoUrl,
      },
    })
  }, [videoId])

  const sendVideoErrorAnalyticsEvent = useCallback(
    (errorId: number) => {
      sendAnalyticsEvent({
        object: eventObject.VIDEO,
        action: eventAction.ERROR,
        video: {
          id: analyticsVideoId,
          link: videoUrl,
          errorId: errorId,
          errorMsg: playerErrors[errorId as keyof PlayerErrorsType],
        },
      })
    },
    [videoId],
  )

  return (
    <MediaPlayerBackground $isPopup={true}>
      <MediaPlayerWrapper
        defaultFocusChild={1}
        focusId={focusIds.FULLSCREEN_PLAYER}
        isTrap
      >
        <YoutubePlayerPopupWrapper>
          <YouTube
            opts={opts}
            onReady={onPlayerReady}
            onError={onPlayerError}
            onStateChange={onPlayerStateChange}
          />
        </YoutubePlayerPopupWrapper>

        <YouTubeControls
          videoElementRef={player}
          playing={playing}
          title={name}
          description={description?.short || description?.long}
          videoId={videoId}
          videoName={name}
          isVideoStarted={isVideoStarted}
          videoUrl={videoUrl}
        />
      </MediaPlayerWrapper>
    </MediaPlayerBackground>
  )
}

export const YouTubePlayer = memo(YouTubePlayerRaw)
